/**
 * lazyWithRetry — resilient dynamic imports.
 *
 * After a deploy, an already-open tab keeps the old entry chunk in memory and
 * tries to fetch route chunks whose hashed filenames no longer exist (404).
 * `React.lazy` then rejects with "Failed to fetch dynamically imported module"
 * and the global ErrorBoundary shows a generic crash screen.
 *
 * Strategy:
 *  1. Retry the import once (handles transient network blips + CDN propagation).
 *  2. If it fails again, reload the page ONCE — guarded by a sessionStorage
 *     marker so a genuinely broken deploy can never produce a reload loop.
 *  3. If we already reloaded in this session, rethrow so the ErrorBoundary can
 *     render its "new version available" message.
 *
 * No business logic lives here.
 */
import { lazy, type ComponentType } from "react";

const RELOAD_FLAG = "mblearn_chunk_reload_at";
const RELOAD_COOLDOWN_MS = 60_000;

const CHUNK_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "failed to load module script",
  "dynamically imported module",
  "chunkloaderror",
  "loading chunk",
  "unable to preload css",
];

/** True when the error looks like a stale/missing bundle chunk. */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const err = error as { name?: string; message?: string };
  const haystack = `${err.name ?? ""} ${err.message ?? String(error)}`.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((p) => haystack.includes(p));
}

function readReloadMark(): number | null {
  try {
    const raw = sessionStorage.getItem(RELOAD_FLAG);
    if (!raw) return null;
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

/** True if we already performed a chunk-recovery reload recently. */
export function hasRecentChunkReload(): boolean {
  const ts = readReloadMark();
  return ts !== null && Date.now() - ts < RELOAD_COOLDOWN_MS;
}

export function markChunkReload(): void {
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* storage unavailable — reload guard degrades to "no reload" below */
  }
}

/**
 * Reload once to pick up the new bundle. Returns false when a reload was
 * already attempted in this session (loop guard) or storage is unavailable.
 */
export function reloadForNewBundle(): boolean {
  if (hasRecentChunkReload()) {
    console.warn("[lazyWithRetry] reload already attempted this session — not reloading again");
    return false;
  }
  markChunkReload();
  if (!hasRecentChunkReload()) {
    // sessionStorage write failed: refuse to reload rather than risk a loop.
    console.warn("[lazyWithRetry] cannot persist reload guard — skipping reload");
    return false;
  }
  console.warn("[lazyWithRetry] stale bundle detected — reloading once");
  try {
    window.location.reload();
  } catch {
    /* noop */
  }
  return true;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function lazyWithRetry<T extends ComponentType<unknown>>(
  importer: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await importer();
    } catch (firstError) {
      console.warn("[lazyWithRetry] dynamic import failed, retrying once", firstError);
      await sleep(300);
      try {
        return await importer();
      } catch (secondError) {
        if (isChunkLoadError(secondError) && reloadForNewBundle()) {
          // Keep the promise pending: the page is unloading.
          return await new Promise<{ default: T }>(() => {});
        }
        throw secondError;
      }
    }
  });
}

/**
 * Vite fires `vite:preloadError` when a modulepreload for a route chunk 404s.
 * Handle it globally so the stale tab recovers before React even renders.
 */
export function installPreloadErrorHandler(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("vite:preloadError", (event) => {
    const payload = (event as unknown as { payload?: unknown }).payload;
    console.warn("[lazyWithRetry] vite:preloadError", payload);
    event.preventDefault();
    reloadForNewBundle();
  });
}
